import { loadStripe } from '@stripe/stripe-js/pure';
import { Elements } from '@stripe/react-stripe-js';
import { useContext, useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';

import usePromise from 'react-use-promise';

import * as api from '../../api';
import * as logger from '../../logger';
import * as rootReducer from '../../reducers/root';
import * as store from '../../reducers/store';

import BalancePage from './BalancePage';
import ConfirmationPage from './ConfirmationPage';
import Loader from '../../components/Loader';
import PaymentInfoProvider, { PaymentContext } from './PaymentInfoProvider';
import PaymentsPage, { PaymentsPageProps } from './PaymentsPage';
import SelectPaymentPage from './SelectPaymentPage';
import PaymentFeedback from './Feedback';
import { parseValue } from 'common/dist/utils/types';
import {
  getSelectedForms,
  getSelectedPracticePreference,
} from '../../utils/formSet';

import styles from './index.module.css';

// publishable key for Notablehealth stripe acct
const PROD_PUBLIC_KEY =
  process.env.NODE_ENV === 'production'
    ? 'pk_live_TZYAWasHYzbbcTEh41w75Iwg00sxuoYMyH'
    : 'pk_test_Zv88F1P9yAQnMTyGnhYbiQyR00Tdo8q2N6';
const TEST_PUBLIC_KEY = 'pk_test_Zv88F1P9yAQnMTyGnhYbiQyR00Tdo8q2N6';

export enum Page {
  Loading = 'loading',
  Error = 'error',
  Balance = 'balance',
  Select = 'select',
  Add = 'add',
  SkipFeedback = 'skip_feedback',
}

export interface PaymentFormFieldValue {
  name?: string;
  selectedIndex?: number;
  skipped?: boolean;
  success?: boolean;
  confirmedWithStatus?: api.Payment.IntentStatus | null;
}

interface StateProps {
  forms: store.Forms;
}

interface DispatchProps {
  setFormStatus: (
    formSetId: string,
    formId: string,
    status: api.PrevisitForm.Status,
  ) => void;
}

interface PageSelectorProps
  extends Omit<PaymentsPageProps, 'onSuccess'>,
    StateProps,
    DispatchProps {
  navigateToNextPage: () => Promise<void>;
  setFormField: (fieldId: string, value: string) => void;
  selectedFormSetId: string;
}

const PageSelectorComponent: React.FC<PageSelectorProps> = ({
  field,
  setFormField,
  selectedFormSetId,
  navigateToNextPage,
  setFormStatus,
  forms,
  ...props
}) => {
  const { t } = useTranslation();
  const [page, setPage] = useState<Page>(Page.Loading);
  const { data: paymentInfo, isLoading, errored } = useContext(PaymentContext);
  const fetchedPayment = paymentInfo !== null;

  useEffect(() => {
    if (isLoading || (!errored && !fetchedPayment)) {
      return setPage(Page.Loading);
    }
    if (
      errored ||
      paymentInfo?.intentStatus !==
        api.Payment.IntentStatus.RequiresPaymentMethod
    ) {
      return setPage(Page.Error);
    }
    return setPage(Page.Balance);
  }, [
    fetchedPayment,
    paymentInfo?.visitFeeAmount,
    paymentInfo?.feeType,
    paymentInfo?.currency,
    paymentInfo?.previousBalance,
    paymentInfo?.applicationFeeAmount,
    paymentInfo?.prepayAmount,
    paymentInfo?.partialBalanceAmount,
    paymentInfo?.intentStatus,
    paymentInfo?.clientSecret,
    isLoading,
    errored,
  ]);

  const updateFormField = async (
    previsitFormSetId: string,
    formFieldId: string,
    value: string,
  ) => {
    try {
      setFormField(field.id, value);
      await api.client.Previsit.updateFormField(
        previsitFormSetId,
        formFieldId,
        value,
      );
    } catch (e) {
      logger.error(e, `[PaymentPageSelectorComponent][updateFormField]`);
    }
  };

  const handleSuccess = async (saveValue: string) => {
    const value = parseValue<PaymentFormFieldValue>(saveValue);
    const successValue = JSON.stringify({ ...value, success: true });
    await updateFormField(field.previsitFormSetId, field.id, successValue);
    await navigateToNextPage();
  };

  const handleSkip = async () => {
    const value = parseValue<PaymentFormFieldValue>(field.value);
    const skippedValue = JSON.stringify({ ...value, skipped: true });
    await updateFormField(field.previsitFormSetId, field.id, skippedValue);
    await navigateToNextPage();
  };

  const handleConfirm = async (status: api.Payment.IntentStatus | null) => {
    const value = parseValue<PaymentFormFieldValue>(field.value);
    const confirmedValue = JSON.stringify({
      ...value,
      confirmedWithStatus: status,
    });
    await updateFormField(field.previsitFormSetId, field.id, confirmedValue);
    await navigateToNextPage();
  };

  switch (page) {
    case Page.Error:
      return <ConfirmationPage onConfirm={handleConfirm} />;
    case Page.Balance:
      return (
        <BalancePage
          infoMessage={field.sublabel ?? undefined}
          navigate={setPage}
          selectedFormSetId={selectedFormSetId}
          paymentPrimitiveConfigs={field.customConfigs}
        />
      );
    case Page.Select:
      return (
        <SelectPaymentPage
          field={field}
          navigate={setPage}
          onSuccess={handleSuccess}
        />
      );
    case Page.Add:
      return (
        <PaymentsPage onSuccess={handleSuccess} field={field} {...props} />
      );
    case Page.SkipFeedback:
      return (
        <PaymentFeedback
          onSubmit={handleSkip}
          feedback={paymentInfo?.patientSkipFeedback}
          selectedFormSetId={selectedFormSetId}
        />
      );
    case Page.Loading:
    default:
      return <div className={styles.Loading}>{t('Loading...')}</div>;
  }
};

const mapStateToProps = (store: store.FormSetSelected): StateProps => ({
  forms: getSelectedForms(store.formSets, store.selectedFormSetId),
});

function mapDispatchToProps(dispatch: Function): DispatchProps {
  return {
    setFormStatus: (
      formSetId: string,
      formId: string,
      status: api.PrevisitForm.Status,
    ) => dispatch(rootReducer.setFormStatus({ formSetId, formId, status })),
  };
}

const PageSelector = connect(
  mapStateToProps,
  mapDispatchToProps,
)(PageSelectorComponent);

interface WrapperStateProps {
  stripeTestModeEnabled: boolean;
  selectedFormSetId: string;
}

export const WrappedPage: React.FC<
  Omit<PageSelectorProps, 'forms' | 'setFormStatus'> & WrapperStateProps
> = ({ stripeTestModeEnabled, selectedFormSetId, ...props }) => {
  const [stripeLoadResult, error, state] = usePromise(async () => {
    const key = stripeTestModeEnabled ? TEST_PUBLIC_KEY : PROD_PUBLIC_KEY;
    const stripe = await loadStripe(key);
    if (!stripe) {
      !stripeTestModeEnabled &&
        logger.error(new Error('Failed to load stripe on payments page'));
      throw Error('failed to load stripe JS');
    }
    return stripe;
  }, [stripeTestModeEnabled]);

  if (error) {
    return (
      <>Failed to load page. Please disable any adblockers and try again.</>
    );
  }

  if (state === 'pending') return <Loader />;

  return (
    <Elements stripe={stripeLoadResult}>
      <PaymentInfoProvider selectedFormSetId={selectedFormSetId}>
        <PageSelector selectedFormSetId={selectedFormSetId} {...props} />
      </PaymentInfoProvider>
    </Elements>
  );
};

const ConnectedWrappedPage = connect(
  (store: store.FormSetSelected): WrapperStateProps => ({
    selectedFormSetId: store.selectedFormSetId,
    stripeTestModeEnabled:
      getSelectedPracticePreference(store).stripeTestModeEnabled,
  }),
)(WrappedPage);

export default ConnectedWrappedPage;
